import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import cn from 'classnames';
import { Spinner } from 'components';
import { ButtonV3, InputV2 } from 'components/ComponentV2';
import Icon from 'components/Icon';
import { Button } from 'components/form';

import AddOrEditLinkModal from 'components/AddOrEditLinkModal';
import { API_ROUTES } from 'lib/api-routes';
import { CustomLinksAttributesWithSelection } from 'lib/atoms/signUpSocietyAtom';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { TUserApiResponse } from 'lib/models/User.model';
import { StudentSocietyResponse } from 'lib/models/student-society';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { apiInstance } from 'lib/utils/axios';
import { parseArrayResponse, parseErrorResponse } from 'lib/utils/parser';
import { REGEX } from 'lib/utils/regex';

import Footer from '../Footer';
import Header from '../Header';
import StepHeading from '../StepHeading';
import LinkCard from './CustomLinkCard';
import styles from './Step5.module.scss';

interface Step5Props {
  progress: number;
  setStep: (step: number) => void;
}

export const Step5: React.FC<Step5Props> = ({ progress, setStep }) => {
  const { updateUserApiResponse, managerSociety } = useAuth();
  const [t] = useTranslation('signup.manager');

  const customLinkSociety = parseArrayResponse(
    managerSociety?.custom_links
  ).map((linkObj) => {
    return { ...linkObj, selected: true };
  });
  const [linkTreeResultSuccessful, setLinkTreeResultSuccessful] =
    React.useState<boolean | undefined>();

  const [noOfLinkTreeLink, setNoOfLinkTreeLink] = React.useState<number>(0);

  const [linkTreeFetchError, setLinkTreeFetchError] =
    React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [customLink, setCustomLink] = React.useState<
    CustomLinksAttributesWithSelection[] | undefined
  >(customLinkSociety);
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [modalEditIndex, setModalEditIndex] = React.useState<number>();
  const [addLinkManually, setAddLinkManually] = React.useState<boolean>(
    customLinkSociety?.length ? true : false
  );

  const {
    register,
    formState: { errors, isSubmitting },
    getValues,
    setError,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      link_tree_url: '',
    },
  });

  const [showError, setShowError] = React.useState<string>('');

  const handleSubmit = async () => {
    const linksToDelete =
      customLink
        ?.filter((link) => !link.selected && link.id)
        .map((link) => ({ ...link, _destroy: true })) || [];
    const linkToUpdate = customLink?.filter((link) => link.selected) || [];

    const body = {
      student_society: {
        custom_links_attributes: [...linkToUpdate, ...linksToDelete],
      },
    };

    try {
      await apiInstance.patch<StudentSocietyResponse>(
        API_ROUTES.STUDENT_SOCIETY,
        body
      );
      updateStepPreComplete();
    } catch (error) {
      const message = parseErrorResponse(error as Error);
      setShowError(message);
    }
  };

  const updateStepPreComplete = async () => {
    const manager = { flow_state: ONBOARDING_FLOW_STATE.PRE_COMPLETE };
    const { data: response } = await apiInstance.patch<TUserApiResponse>(
      API_ROUTES.MANAGER_USER,
      {
        manager,
      }
    );
    updateUserApiResponse(response);
    setStep(6);
  };

  useEffect(() => {
    const subscription = watch((value) => {
      const { link_tree_url } = value;
      if (link_tree_url !== undefined) {
        setError('link_tree_url', {});
        setLinkTreeFetchError('');
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const fetchLinkTree = async () => {
    const linkTreeUrl = getValues('link_tree_url');
    if (!REGEX.URL.test(linkTreeUrl)) {
      setError('link_tree_url', { message: t('invalid-url') });
      return;
    }
    setIsLoading(true);
    try {
      const { data: response } = await apiInstance.post<
        { url: string; title: string }[]
      >(API_ROUTES.FETCH_LINK_TREE, { linktree_url: linkTreeUrl });

      if (response.length) {
        setNoOfLinkTreeLink(response.length);
        setLinkTreeResultSuccessful(true);
        setCustomLink(
          response.map(
            (link) =>
              ({
                link: link.url,
                title: link.title,
                selected: false,
              } as CustomLinksAttributesWithSelection)
          )
        );
      } else {
        handleLinkTreeFail();
      }
    } catch (error) {
      console.error(error);
      handleLinkTreeFail();
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkTreeFail = () => {
    setLinkTreeFetchError(t('step-5.link-tree.fetch-error'));
    setLinkTreeResultSuccessful(false);
    setCustomLink(undefined);
    setNoOfLinkTreeLink(0);
  };

  const saveLink = (title: string, link: string, index?: number) => {
    if (index !== undefined && customLink) {
      const newCustomLink = [...customLink];
      newCustomLink[index] = {
        ...customLink[index],
        title,
        link,
      };
      setCustomLink(newCustomLink);
    } else {
      setCustomLink([
        ...(customLink || []),
        {
          title,
          link,
          selected: true,
        } as CustomLinksAttributesWithSelection,
      ]);
    }
  };

  const deleteLink = (index: number) => {
    if (customLink) {
      const newCustomLink = [...customLink];
      newCustomLink[index] = {
        ...customLink[index],
        selected: true,
        _destroy: true,
      };

      setCustomLink(newCustomLink);
    }

    setIsModalOpen(false);
    setModalEditIndex(undefined);
  };

  const changeEmojiAndSelected = (
    index: number,
    selected: boolean,
    emoji?: string
  ) => {
    if (customLink) {
      const newCustomLink = [...customLink];
      newCustomLink[index] = {
        ...customLink[index],
        emoji,
        selected,
      } as CustomLinksAttributesWithSelection;
      setCustomLink(newCustomLink);
    }
  };

  return (
    <>
      <Header
        percentComplete={progress}
        onBack={() => {
          if (addLinkManually) {
            setAddLinkManually(false);
            setLinkTreeResultSuccessful(undefined);
            setLinkTreeFetchError('');
            setNoOfLinkTreeLink(0);
            setCustomLink(undefined);
            setValue('link_tree_url', '');
          } else {
            setStep(4);
          }
        }}
      />
      <div className={styles.stepContent}>
        <StepHeading
          title={
            addLinkManually
              ? t('step-5.header.title-add-manually')
              : t('step-5.header.title')
          }
          desc={
            addLinkManually
              ? t('step-5.header.desc-add-manually')
              : t('step-5.header.desc')
          }
        />
        {!addLinkManually && (
          <>
            <InputV2
              {...register('link_tree_url', {
                pattern: {
                  value: REGEX.URL,
                  message: t('invalid-url'),
                },
              })}
              labelClassName={styles.fieldLabel}
              placeholder={t('step-5.link-tree.placeholder')}
              isInvalid={
                (!!errors.link_tree_url?.message || !!linkTreeFetchError) &&
                !isLoading
              }
              showEnterText={
                linkTreeResultSuccessful === undefined && !isLoading
                  ? 'import'
                  : undefined
              }
              onKeyDown={async (event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  fetchLinkTree();
                }
              }}
              className={cn({
                [styles.successInput]: !isLoading && linkTreeResultSuccessful,
              })}
              rightIcon={
                isLoading ? (
                  <Spinner size="xsmall" />
                ) : (
                  linkTreeResultSuccessful && (
                    <Icon
                      iconName={'icon_check-double'}
                      className={styles.linkTreeSuccessIcon}
                    />
                  )
                )
              }
              disabled={isLoading}
            />
            {errors.link_tree_url?.message && (
              <div className={styles.errorMessage}>
                {errors.link_tree_url?.message}
              </div>
            )}
            {linkTreeFetchError && (
              <div className={styles.errorMessage}>{linkTreeFetchError}</div>
            )}
          </>
        )}

        {linkTreeResultSuccessful === true ? (
          <>
            <div className={styles.sectionSeparator}></div>
            <div className={styles.linkTreeSuccessText}>
              {t('step-5.link-tree.success-text', { noOfLinkTreeLink })}
            </div>
          </>
        ) : null}

        {isModalOpen && (
          <AddOrEditLinkModal
            saveLink={saveLink}
            isEdit={modalEditIndex !== undefined}
            onClose={() => {
              setIsModalOpen(false);
              setModalEditIndex(undefined);
            }}
            index={modalEditIndex}
            deleteLink={deleteLink}
            title={
              modalEditIndex !== undefined
                ? customLink?.[modalEditIndex]?.title
                : undefined
            }
            link={
              modalEditIndex !== undefined
                ? customLink?.[modalEditIndex]?.link
                : undefined
            }
          />
        )}
        {customLink?.length ? (
          <div className={styles.customLinkContainer}>
            {customLink.map((link, index) => (
              <>
                {link._destroy ? (
                  <></>
                ) : (
                  <LinkCard
                    key={index}
                    link={link}
                    index={index}
                    onCardClick={() => {
                      setModalEditIndex(index);
                      setIsModalOpen(true);
                    }}
                    changeLinkData={changeEmojiAndSelected}
                  />
                )}
              </>
            ))}
          </div>
        ) : null}
        {linkTreeResultSuccessful !== undefined &&
          (customLink?.length ? (
            <Button
              type="button"
              color="secondary"
              className={cn(
                styles.whiteButton,
                styles.mt16,
                styles.addMoreLink
              )}
              onClick={() => {
                setIsModalOpen(true);
              }}
            >
              {t('step-5.add-link')}
            </Button>
          ) : (
            <Button
              type="button"
              color="secondary"
              className={cn(styles.whiteButton, styles.mt42)}
              onClick={() => {
                setIsModalOpen(true);
                setAddLinkManually(true);
              }}
              isFullWidth
            >
              {t('step-5.add-link-manually')}
            </Button>
          ))}
        {showError && <div className={styles.errorMessage}>{showError}</div>}
        {
          <Footer>
            {linkTreeResultSuccessful !== true && !addLinkManually ? (
              <Button
                type="button"
                color="secondary"
                className={styles.whiteButton}
                onClick={updateStepPreComplete}
                isFullWidth
              >
                {t('step-5.add-links-later')}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  color="secondary"
                  className={styles.whiteButton}
                  onClick={() => {
                    const slug = managerSociety?.slug;
                    const win = window.open(
                      `${PAGE_ROUTES.SOCIETIES}/${slug}`,
                      '_blank'
                    );
                    win?.focus();
                  }}
                  isFullWidth
                >
                  {t('step-5.preview-profile')}
                </Button>
                <ButtonV3
                  type="submit"
                  isFullWidth
                  isLoading={isSubmitting}
                  size="xlarge"
                  onClick={handleSubmit}
                >
                  {t('next')}
                </ButtonV3>
              </>
            )}
          </Footer>
        }
      </div>
    </>
  );
};
